// Active Availability editor.
import { useTranslation } from "react-i18next";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import { AppStatusChip } from "../../design-system/components/AppChip";
import AppDivider from "../../design-system/components/AppDivider";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppSelect from "../../design-system/components/AppSelect";
import AppTabs from "../../design-system/components/AppTabs";
import AppText from "../../design-system/components/AppText";
import AppTextarea from "../../design-system/components/AppTextarea";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  IconBlock,
  IconClose,
  IconHoliday,
  IconSuccess,
  IconTime,
  IconTimetable,
} from "../../design-system/icons";
import AvailabilityDateField from "./AvailabilityDateField";

const STATUS_ICONS = {
  availability: IconSuccess,
  absence: IconBlock,
  holiday: IconHoliday,
};

const TIME_INPUT_PROPS = {
  inputMode: "numeric",
  pattern: "[0-9]{2}:[0-9]{2}",
  maxLength: 5,
  placeholder: "HH:MM",
};

function formatTimeInputValue(value) {
  const raw = String(value || "").replace(/\D/g, "").slice(0, 4);
  if (raw.length <= 2) return raw;
  return `${raw.slice(0, 2)}:${raw.slice(2)}`;
}

function displayTimeInputValue(value, fallback = "") {
  const text = String(value || "");
  if (/^\d{0,2}$/.test(text) || /^\d{2}:\d{0,1}$/.test(text)) return text;
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : fallback;
}

function formatTime(value, fallback) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : fallback;
}

export default function AvailabilityEditor({
  dateKey,
  mode,
  draft,
  setDraft,
  dayData,
  holidayTypes = [],
  onClose,
  selectedItems = [],
  onRemoveSelected,
  commentLabel,
}) {
  const { t } = useTranslation();
  const statusLabels = {
    availability: t("availability.status.available"),
    absence: t("availability.status.absent"),
    holiday: t("availability.status.holiday"),
  };
  const resolvedCommentLabel = commentLabel || t("availability.editor.comment");
  const isLocked = dayData?.is_editable === false;
  const isHoliday = draft.entry_kind === "holiday";
  const usesTimes = !draft.is_full_day && ["availability", "absence"].includes(draft.entry_kind);
  const selectedHolidayType = holidayTypes.find(
    (type) => String(type.id) === String(draft.holiday_type_id || holidayTypes[0]?.id || "")
  );
  const statusOrder = mode === "availability"
    ? ["availability", "absence", "holiday"]
    : ["absence", "holiday", "availability"];
  const statusItems = statusOrder.map((key) => {
    const StatusIcon = STATUS_ICONS[key];
    return {
      key,
      label: (
        <span className={`availability-editor__status-label availability-editor__status-label--${key}`}>
          <StatusIcon />
          <span>{statusLabels[key]}</span>
        </span>
      ),
    };
  });
  const selectedGroups = [...selectedItems.reduce((groups, item) => {
    const userKey = item.userId || item.userName;
    const group = groups.get(userKey) || {
      key: userKey,
      userName: item.userName,
      dates: new Map(),
    };
    const dateKey = item.dateKey || item.dateLabel || item.label;
    const date = group.dates.get(dateKey) || { label: item.dateLabel || item.label, ids: [] };
    date.ids.push(item.id);
    group.dates.set(dateKey, date);
    groups.set(userKey, group);
    return groups;
  }, new Map()).values()].map((group) => ({
    ...group,
    dates: [...group.dates.values()],
  }));
  function updateStatus(entryKind) {
    setDraft((current) => ({
      ...current,
      entry_kind: entryKind,
      holiday_type_id:
        entryKind === "holiday"
          ? current.holiday_type_id || holidayTypes[0]?.id || ""
          : current.holiday_type_id,
      is_full_day: entryKind === "holiday" ? true : current.is_full_day,
    }));
  }

  return (
    <AppCard className="availability-editor">
      <AppCardHeader
        title={t("availability.editor.title")}
        action={(
          <AppTooltip content={t("availability.editor.close")}>
            <AppButton variant="neutral" size="md" iconOnly onClick={onClose} aria-label={t("availability.editor.close")}>
              <IconClose />
            </AppButton>
          </AppTooltip>
        )}
      />

      {/* Der Speicherstand steht im Toast, nicht als Band im Panel — er hat
          hier dauerhaft Platz gekostet, obwohl er nur kurz interessiert. */}
      <AppCardBody inset="none" className="availability-editor__body">
        <AppFieldStack className="availability-editor__areas">
          {selectedItems.length > 0 ? (
            <div className="availability-editor__selection">
              {selectedGroups.map((group) => (
                <div className="availability-editor__selection-row" key={group.key}>
                  <AppText
                    className="availability-editor__selection-user"
                    variant="label-2"
                    title={group.userName}
                  >
                    {group.userName}
                  </AppText>
                  <div className="availability-editor__selection-dates">
                    {group.dates.map((item) => (
                      <AppButton
                        key={item.ids.join("::")}
                        variant="muted"
                        size="xs"
                        onClick={() => onRemoveSelected?.(item.ids)}
                        aria-label={t("availability.editor.remove_selection", { label: item.label })}
                      >
                        <span>{item.label}</span>
                        <IconClose sx={{ fontSize: 16 }} />
                      </AppButton>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {isLocked ? (
            <AppCallout
              variant="warning"
              title={t("availability.editor.locked_title")}
              description={dayData?.blocked_message || t("availability.editor.locked_description")}
            />
          ) : null}

          <AppDivider spacing="md" />

          <section className="availability-editor__section">
            <AppText as="h4" variant="heading-4">{t("availability.editor.status")}</AppText>
            <AppTabs
              items={statusItems}
              activeKey={draft.entry_kind}
              onChange={(entryKind) => {
                if (!isLocked) updateStatus(entryKind);
              }}
              variant="contained"
              className={`availability-editor__status-bar${isLocked ? " is-disabled" : ""}`}
              aria-label={t("availability.editor.status")}
              aria-disabled={isLocked ? "true" : undefined}
            />
          </section>

          <AppDivider spacing="md" />

          <section className="availability-editor__section">
            <AppText as="h4" variant="heading-4">{t("availability.editor.period")}</AppText>

            {isHoliday ? (
              <AppFieldStack>
                <AppFieldGrid columns={2} size="md">
                  <AvailabilityDateField
                    label={t("availability.editor.from")}
                    value={draft.start_date || dateKey}
                    disabled={isLocked}
                    onChange={(nextStartDate) =>
                      setDraft((current) => ({
                        ...current,
                        start_date: nextStartDate,
                        end_date:
                          current.end_date && current.end_date >= nextStartDate
                            ? current.end_date
                            : nextStartDate,
                      }))
                    }
                  />
                  <AvailabilityDateField
                    label={t("availability.editor.until")}
                    min={draft.start_date || dateKey}
                    value={draft.end_date || draft.start_date || dateKey}
                    disabled={isLocked}
                    onChange={(endDate) =>
                      setDraft((current) => ({ ...current, end_date: endDate }))
                    }
                  />
                </AppFieldGrid>

                <AppField label={t("availability.editor.holiday_type")} size="md" layout="stacked">
                  <AppSelect
                    options={holidayTypes.map((type) => ({ value: String(type.id), label: type.name }))}
                    value={String(draft.holiday_type_id || holidayTypes[0]?.id || "")}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, holiday_type_id: event.target.value }))
                    }
                    searchable
                    placeholder={t("availability.editor.select_holiday_type")}
                    searchPlaceholder={t("availability.editor.search_holiday_type")}
                    emptyMessage={t("availability.editor.no_holiday_type_found")}
                    disabled={isLocked}
                  />
                </AppField>

                {selectedHolidayType ? (
                  <AppCallout
                    variant="info"
                          description={t("availability.editor.holiday_type_details", {
                      hours: selectedHolidayType.hours_per_day,
                      start: formatTime(selectedHolidayType.start_time, "00:00"),
                      end: formatTime(selectedHolidayType.end_time, "23:59"),
                    })}
                  />
                ) : (
                  <AppCallout
                    variant="warning"
                          description={t("availability.editor.no_holiday_type")}
                  />
                )}
              </AppFieldStack>
            ) : (
              <AppFieldStack>
                <AppTabs
                  items={[
                    {
                      key: "full-day",
                      label: (
                        <span className="availability-editor__status-label">
                          <IconTimetable />
                          <span>{t("availability.editor.full_day")}</span>
                        </span>
                      ),
                    },
                    {
                      key: "custom",
                      label: (
                        <span className="availability-editor__status-label">
                          <IconTime />
                          <span>{t("availability.editor.custom")}</span>
                        </span>
                      ),
                    },
                  ]}
                  activeKey={draft.is_full_day ? "full-day" : "custom"}
                  onChange={(value) => {
                    if (!isLocked) {
                      setDraft((current) => ({ ...current, is_full_day: value === "full-day" }));
                    }
                  }}
                  variant="contained"
                  className={`availability-editor__status-bar${isLocked ? " is-disabled" : ""}`}
                  aria-label={t("availability.editor.period")}
                  aria-disabled={isLocked ? "true" : undefined}
                />

                {usesTimes ? (
                  <AppFieldGrid columns={2} size="md">
                    <AppField label={t("availability.editor.from")} size="md" layout="stacked">
                      <AppInput
                        type="text"
                        value={displayTimeInputValue(draft.start_time, "08:00")}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, start_time: formatTimeInputValue(event.target.value) }))
                        }
                        disabled={isLocked}
                        {...TIME_INPUT_PROPS}
                      />
                    </AppField>
                    <AppField label={t("availability.editor.until")} size="md" layout="stacked">
                      <AppInput
                        type="text"
                        value={displayTimeInputValue(draft.end_time, "17:00")}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, end_time: formatTimeInputValue(event.target.value) }))
                        }
                        disabled={isLocked}
                        {...TIME_INPUT_PROPS}
                      />
                    </AppField>
                  </AppFieldGrid>
                ) : null}
              </AppFieldStack>
            )}

            {!isHoliday ? (
              <AppField label={resolvedCommentLabel} size="md" layout="stacked">
                <AppTextarea
                  size="md"
                  rows={3}
                  resize="none"
                  value={draft.comment}
                  disabled={isLocked}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, comment: event.target.value }))
                  }
                />
              </AppField>
            ) : null}

            {dayData?.is_pending ? (
              <AppStatusChip status="warning" size="md" leadingIcon={<IconTime />}>
                {t("availability.editor.waiting_for_approval")}
              </AppStatusChip>
            ) : null}
          </section>
        </AppFieldStack>
      </AppCardBody>
    </AppCard>
  );
}
